var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// db/analytics.ts
import { A } from "@adornis/base/env-info.js";
import { Entity, Field } from "@adornis/baseql/decorators.js";
import { constructValue } from "@adornis/baseql/entities/construct.js";
import { MongoEntity } from "@adornis/baseql/entities/mongoEntity.js";
import { getCollectionHandle, getRawCollection } from "@adornis/baseql/server/collections.js";
import { publishFilter } from "@adornis/baseql/server/context.js";
import { validate } from "@adornis/validation/decorators.js";
import { nonOptional } from "@adornis/validation/functions/nonOptional.js";
import { ObjectId } from "mongodb";
import { firstValueFrom, from } from "rxjs";
import { filter, map, switchMap, tap } from "rxjs/operators";
import { QueueJob } from "./queue-job.js";
if (A.isServer) {
  const analyticsDB = getRawCollection("analytics");
  analyticsDB.then((db) => {
    db.createIndex({ type: 1, user: 1 });
    db.createIndex({ reference: 1, user: 1, type: 1 });
    db.createIndex({ reference: 1, type: 1 });
    db.createIndex({ user: 1, type: 1 });
    db.createIndex({ createdAt: -1 });
  });
}
var Analytics = class extends MongoEntity {
  get invalid() {
    return this._i;
  }
  constructor(doc) {
    super(doc);
    if (!this.createdAt)
      this.createdAt = /* @__PURE__ */ new Date();
    if (!this.updatedAt)
      this.updatedAt = /* @__PURE__ */ new Date();
    if (this._i === void 0 || this._i === null)
      this._i = true;
    this.commit();
  }
  save() {
    throw new Error("dont call save on analytics directly");
  }
  remove() {
    throw new Error("remove is removed for analytics");
  }
  /**
   * please overwrite
   * @param logs
   */
  static async createQueueJob(log) {
    throw new Error("not implemented");
  }
  /**
   * please overwrite
   * @param queueJob
   */
  static updateAnalytic(queueJob) {
    throw new Error("not implemented");
  }
  static async saveEntityWithoutContext(analytic) {
    const collection = await getRawCollection(this._collectionName);
    if (!analytic._id)
      analytic._id = analytic.constructor.resolveID(analytic) || A.getGloballyUniqueID();
    const result = await collection.updateOne({ _id: analytic._id }, { $set: analytic.toJSON() }, { upsert: true });
    await this.afterUpdate({ _id: analytic._id, _class: analytic._class }, analytic, result);
  }
  static async retrieveOrCreateAnalytic(userID, initialValues) {
    const coll = await getRawCollection(this._collectionName);
    const query = { userID, _class: this._class, ...initialValues };
    await coll.updateOne(
      query,
      {
        $setOnInsert: {
          _id: new ObjectId().toHexString(),
          userID,
          _class: this._class,
          createdAt: /* @__PURE__ */ new Date(),
          updatedAt: /* @__PURE__ */ new Date(),
          _i: true,
          ...initialValues
        }
      },
      { upsert: true }
    );
    return constructValue(await coll.findOne(query));
  }
  static subscribeAnalytic(selector) {
    const query = { ...selector, _class: this._class };
    const handle = getCollectionHandle(this._collectionName);
    const subscription = from(publishFilter(this._class)).pipe(
      switchMap((filter2) => {
        return handle.watchQuery(filter2 ? { $and: [filter2, query] } : query);
      }),
      map((results) => {
        return results.map((result) => constructValue(result));
      }),
      tap(async (results) => {
        const invalidResults = results.filter((res) => res.invalid);
        if (invalidResults.length) {
          QueueJob.createQueueJobServerSide(
            new QueueJob({
              analyticIDs: invalidResults.map((res) => res._id),
              analyticsClass: this._class,
              urgency: 1,
              targetDate: /* @__PURE__ */ new Date()
            })
          );
        }
      })
    );
    return subscription;
  }
  static getValidAnalytics(selector) {
    return this.subscribeAnalytic(selector).pipe(filter((results) => results.every((result) => !result.invalid)));
  }
  static async retrieveOrCreateValidAnalytic(userID, initialValues) {
    const analytic = await this.retrieveOrCreateAnalytic(userID, initialValues);
    if (!analytic.invalid) {
      return analytic;
    }
    const validAnalytic = (await firstValueFrom(this.getValidAnalytics({ _id: analytic._id })))[0];
    return validAnalytic;
  }
  static async retrieveAnalytic(selector) {
    const coll = await getRawCollection(Analytics._collectionName);
    return constructValue(await coll.findOne({ _class: this._class, ...selector }));
  }
  static async retrieveAnalytics(selector) {
    const coll = await getRawCollection(Analytics._collectionName);
    return (await coll.find({ _class: this._class, ...selector }).toArray()).map((a) => constructValue(a));
  }
};
Analytics._class = "Analytics";
Analytics._collectionName = "analytics";
__decorateClass([
  validate(nonOptional()),
  Field(() => String)
], Analytics.prototype, "userID", 2);
__decorateClass([
  Field(() => Date)
], Analytics.prototype, "createdAt", 2);
__decorateClass([
  Field(() => Date)
], Analytics.prototype, "updatedAt", 2);
__decorateClass([
  Field(() => Boolean)
], Analytics.prototype, "_i", 2);
Analytics = __decorateClass([
  Entity()
], Analytics);
if (A.isClient)
  window.Analytics = Analytics;
export {
  Analytics
};
